import { format } from "date-fns";

/**
 * A document carries two different dates, and the lists show both:
 *
 * - **Uploaded** — `created_at`, when the file reached Franklin. Shown in full
 *   (date and time) on each row's meta line.
 * - **File modified** — the creation/modification date embedded in the file's
 *   own metadata, which can predate the upload by years. Shown in the "File
 *   modified" column and placed on by the Timeline.
 *
 * Their tooltips explain which is which, so the source of the second one has to
 * be known, not just its value.
 */

/** Where the "File modified" value came from. */
export type FileDateSource = "modified" | "created" | "upload";

interface FileDateFields {
  created_at?: string | null;
  original_created_at?: string | null;
  document_content_modified_date?: string | null;
}

/**
 * The "File modified" date and its source — the same
 * `document_content_modified_date → original_created_at → created_at` chain as
 * `effective_modified_at` (see `getDocTimestamp`), with the link that answered.
 */
export function resolveFileDate(
  doc: FileDateFields
): { iso: string; source: FileDateSource } | null {
  if (doc.document_content_modified_date) {
    return { iso: doc.document_content_modified_date, source: "modified" };
  }
  if (doc.original_created_at) return { iso: doc.original_created_at, source: "created" };
  if (doc.created_at) return { iso: doc.created_at, source: "upload" };
  return null;
}

function formatOrDash(value: string | Date | null | undefined, pattern: string): string {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : format(date, pattern);
}

/** `03-09-2026` — the list's date format. */
export function formatDocumentDate(value: string | Date | null | undefined): string {
  return formatOrDash(value, "dd-MM-yyyy");
}

/** `03-09-2026 14:05` — the list's date format with a 24-hour time. */
export function formatDocumentDateTime(value: string | Date | null | undefined): string {
  return formatOrDash(value, "dd-MM-yyyy HH:mm");
}

interface FileDateHintCopy {
  fileModifiedTooltip: string;
  fileCreatedTooltip: string;
  fileDateMissingTooltip: string;
}

/** The tooltip on a "File modified" date: which metadata date it is, in full. */
export function fileDateHint(
  resolved: { iso: string; source: FileDateSource },
  copy: FileDateHintCopy
): string {
  const template =
    resolved.source === "modified"
      ? copy.fileModifiedTooltip
      : resolved.source === "created"
        ? copy.fileCreatedTooltip
        : copy.fileDateMissingTooltip;
  return template.replace("{date}", formatDocumentDateTime(resolved.iso));
}
